import React from 'react';
import { Box, ButtonBase, Stack, Typography, alpha } from '@mui/material';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { CHECKOUT_COPY } from 'lib/billing';

const QTY_COPY = CHECKOUT_COPY.qty;

type Variant = 'tier' | 'mini';

type Props = {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  ariaLabel?: string;
  variant?: Variant;
  disabled?: boolean;
  /**
   * Tiered "block-then-unit" stepping. When provided and > 1:
   *   - At v=0: increment jumps to `firstStep` (skipping the dead 1..firstStep-1 range
   *     that a flat-block tier already covers).
   *   - At v=firstStep: decrement drops back to 0.
   *   - Above `firstStep`: increment/decrement step by 1 (per-unit overage tier).
   * Without it, steps are ±1.
   */
  firstStep?: number;
};

// Marketing's TierStepper: pill container w/ white circle buttons + shadow.
// Marketing's MiniStepper: standalone bordered buttons that hover teal.
export function QtyStepper({ value, onChange, min = 0, max = 999, ariaLabel, variant = 'tier', disabled, firstStep }: Props) {
  const v = Number(value) || 0;
  const lot = firstStep && firstStep > 1 ? firstStep : 0;
  const decDisabled = disabled || v <= min;
  const incDisabled = disabled || v >= max;
  const nextDown = (): number => {
    if (lot && v <= lot) return Math.max(min, 0);
    return Math.max(min, v - 1);
  };
  const nextUp = (): number => {
    if (lot && v < lot) return Math.min(max, lot);
    return Math.min(max, v + 1);
  };
  const dec = () => !decDisabled && onChange(nextDown());
  const inc = () => !incDisabled && onChange(nextUp());

  if (variant === 'mini') {
    return (
      <Stack direction="row" alignItems="center" spacing={1} aria-label={ariaLabel}>
        <MiniButton onClick={dec} disabled={decDisabled} label={QTY_COPY.decrease}>
          <MinusOutlined style={{ fontSize: 12, strokeWidth: 40 }} />
        </MiniButton>
        <Typography
          component="span"
          sx={{
            minWidth: 24,
            textAlign: 'center',
            fontSize: 14,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1
          }}
        >
          {v}
        </Typography>
        <MiniButton onClick={inc} disabled={incDisabled} label={QTY_COPY.increase}>
          <PlusOutlined style={{ fontSize: 12, strokeWidth: 40 }} />
        </MiniButton>
      </Stack>
    );
  }

  return (
    <Box
      aria-label={ariaLabel}
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        p: 0.5,
        borderRadius: 999,
        bgcolor: alpha(theme.palette.text.primary, 0.04)
      })}
    >
      <PillButton onClick={dec} disabled={decDisabled} label={QTY_COPY.decrease}>
        <MinusOutlined style={{ fontSize: 14, strokeWidth: 40 }} />
      </PillButton>
      <Typography
        component="span"
        sx={{
          minWidth: 36,
          textAlign: 'center',
          fontSize: 16,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1,
          color: 'text.primary'
        }}
      >
        {v}
      </Typography>
      <PillButton onClick={inc} disabled={incDisabled} label={QTY_COPY.increase}>
        <PlusOutlined style={{ fontSize: 14, strokeWidth: 40 }} />
      </PillButton>
    </Box>
  );
}

function PillButton({
  onClick,
  disabled,
  label,
  children
}: {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': { boxShadow: '0 2px 6px rgba(0,0,0,0.12)' },
        '&:active': { transform: 'scale(0.92)' },
        '&.Mui-disabled': {
          bgcolor: 'transparent',
          color: 'text.disabled',
          boxShadow: 'none'
        }
      }}
    >
      {children}
    </ButtonBase>
  );
}

function MiniButton({
  onClick,
  disabled,
  label,
  children
}: {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'transform 120ms ease, border-color 120ms ease, color 120ms ease',
        '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
        '&:active': { transform: 'scale(0.92)' },
        '&.Mui-disabled': {
          opacity: 0.4,
          borderColor: 'divider',
          color: 'text.disabled'
        }
      }}
    >
      {children}
    </ButtonBase>
  );
}
